import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  creditText,
  dateText,
  errText,
  fullName,
  platformApi,
  type AdminTenantDetail,
  type QuotaLimits,
  type TenantStatus,
} from "@/admin/api";
import { NativeSelect, Pill, TenantStatusPill } from "@/admin/components";
import ApiKeysPanel from "@/components/ApiKeysPanel";

export default function AdminTenantDetailPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["admin", "tenant", id], queryFn: () => platformApi.tenant(id) });

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!q.data) {
    return <p className="text-sm text-muted-foreground">Tenant not found.</p>;
  }
  return <TenantDetail tenant={q.data} />;
}

function useTenantSaver(id: string) {
  const qc = useQueryClient();
  return (data: AdminTenantDetail) => {
    qc.setQueryData(["admin", "tenant", id], data);
    qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
  };
}

function TenantDetail({ tenant }: { tenant: AdminTenantDetail }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const saved = useTenantSaver(tenant.id);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const setStatus = useMutation({
    mutationFn: (status: TenantStatus) => platformApi.updateTenant(tenant.id, { status }),
    onSuccess: (t) => {
      saved(t);
      toast.success(`Status set to ${t.status}.`);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const remove = useMutation({
    mutationFn: () => platformApi.deleteTenant(tenant.id),
    onSuccess: () => {
      toast.success("Tenant deleted.");
      qc.invalidateQueries({ queryKey: ["admin"] });
      navigate("/admin/tenants");
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/tenants" className="inline-flex items-center text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="mr-1 h-4 w-4" /> Tenants
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{tenant.name}</h1>
            <TenantStatusPill status={tenant.status} />
          </div>
          <div className="flex items-center gap-2">
            <NativeSelect
              value={tenant.status}
              onChange={(v) => setStatus.mutate(v as TenantStatus)}
              disabled={setStatus.isPending}
              className="w-40"
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </NativeSelect>
            <Button variant="outline" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {tenant.slug} · created {new Date(tenant.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
          {tenant.parent && <> · subaccount of {tenant.parent.name}</>}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <DetailsCard tenant={tenant} />
        <BillingCard tenant={tenant} />
      </div>
      <SubscriptionCard tenant={tenant} />
      <LimitsCard tenant={tenant} />
      <MembersCard tenant={tenant} />
      <ApiKeysCard tenant={tenant} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {tenant.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The tenant disappears from the platform and its users can no longer reach it. Its data
              stays in the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()}>Delete tenant</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DetailsCard({ tenant }: { tenant: AdminTenantDetail }) {
  const saved = useTenantSaver(tenant.id);
  const initial = {
    name: tenant.name,
    slug: tenant.slug,
    billing_email: tenant.billing_email,
    timezone: tenant.timezone,
    locale: tenant.locale,
  };
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [tenant.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = (Object.keys(initial) as (keyof typeof initial)[]).some((k) => form[k] !== initial[k]);

  const save = useMutation({
    mutationFn: () => platformApi.updateTenant(tenant.id, form),
    onSuccess: (t) => {
      saved(t);
      toast.success("Saved.");
    },
    onError: (e) => toast.error(errText(e, "Couldn't save.")),
  });

  const field = (k: keyof typeof initial, label: string, type = "text") => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {field("name", "Name")}
        {field("slug", "Slug")}
        {field("billing_email", "Billing email", "email")}
        <div className="grid grid-cols-2 gap-3">
          {field("timezone", "Timezone")}
          {field("locale", "Locale")}
        </div>
        <div className="flex justify-end">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BillingCard({ tenant }: { tenant: AdminTenantDetail }) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const rows: [string, React.ReactNode][] = [
    ["Plan", tenant.plan_name ?? "—"],
    ["Subscription", tenant.subscription_status ?? "—"],
    ["Prepaid credit", creditText(tenant.credit_balance_millicents)],
  ];
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Billing</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setAdjustOpen(true)}>
          Adjust credit
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="divide-y text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between py-2">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="font-medium capitalize">{v}</dd>
            </div>
          ))}
        </dl>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent wallet activity</span>
            <Link
              to={`/admin/credit-transactions?tenant_id=${tenant.id}&tenant_name=${encodeURIComponent(tenant.name)}`}
              className="text-xs hover:underline"
            >
              Full history
            </Link>
          </div>
          {tenant.recent_credit.length === 0 ? (
            <p className="text-sm text-muted-foreground">No wallet activity yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {tenant.recent_credit.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 py-1.5">
                  <div className="min-w-0">
                    <div className="truncate">{c.description || c.kind}</div>
                    <div className="text-xs text-muted-foreground">{dateText(c.created_at)}</div>
                  </div>
                  <span
                    className={
                      c.amount_millicents >= 0 ? "tabular-nums text-emerald-600" : "tabular-nums text-muted-foreground"
                    }
                  >
                    {c.amount_millicents >= 0 ? "+" : "-"}
                    {creditText(Math.abs(c.amount_millicents))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
      {adjustOpen && <AdjustCreditDialog tenant={tenant} onClose={() => setAdjustOpen(false)} />}
    </Card>
  );
}

function AdjustCreditDialog({ tenant, onClose }: { tenant: AdminTenantDetail; onClose: () => void }) {
  const saved = useTenantSaver(tenant.id);
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const adjust = useMutation({
    mutationFn: () => platformApi.adjustCredit(tenant.id, amount, description),
    onSuccess: (t) => {
      saved(t);
      qc.invalidateQueries({ queryKey: ["admin", "rows", "credit-transactions"] });
      toast.success(`Balance is now ${creditText(t.credit_balance_millicents)}.`);
      onClose();
    },
    onError: (e) => toast.error(errText(e, "Couldn't adjust the credit.")),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust credit</DialogTitle>
          <DialogDescription>
            {tenant.name} has {creditText(tenant.credit_balance_millicents)}. A positive amount adds credit, a negative
            amount removes it.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            adjust.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label>Amount (USD)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100" required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown in the tenant's wallet history" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={adjust.isPending}>
              {adjust.isPending ? "Saving…" : "Apply"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SUB_STATUSES = [
  ["active", "Active"],
  ["trialing", "Trialing"],
  ["past_due", "Past due"],
  ["paused", "Paused"],
  ["cancelled", "Cancelled"],
] as const;

function SubscriptionCard({ tenant }: { tenant: AdminTenantDetail }) {
  const saved = useTenantSaver(tenant.id);
  const sub = tenant.subscription;
  const [planId, setPlanId] = useState(sub?.plan_id ?? "");
  const [threshold, setThreshold] = useState(((sub?.auto_topup_threshold_cents ?? 0) / 100).toFixed(2));
  useEffect(() => {
    setPlanId(sub?.plan_id ?? "");
    setThreshold(((sub?.auto_topup_threshold_cents ?? 0) / 100).toFixed(2));
  }, [sub?.plan_id, sub?.auto_topup_threshold_cents]);

  const save = useMutation({
    mutationFn: (d: Parameters<typeof platformApi.updateSubscription>[1]) => platformApi.updateSubscription(tenant.id, d),
    onSuccess: (t) => {
      saved(t);
      toast.success("Subscription saved.");
    },
    onError: (e) => toast.error(errText(e, "Couldn't save the subscription.")),
  });

  const switches: { key: "enforce_minute_limit" | "cancel_at_period_end" | "auto_topup_enabled"; label: string; help: string }[] = [
    { key: "enforce_minute_limit", label: "Enforce minute limit", help: "Block new calls once the plan's minutes are used up." },
    { key: "auto_topup_enabled", label: "Automatic top-up", help: "Charge the saved card when credit drops below the threshold." },
    { key: "cancel_at_period_end", label: "Cancel at period end", help: "Ends the subscription when the current period finishes." },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Subscription</CardTitle>
        <p className="text-sm text-muted-foreground">
          {sub
            ? `${sub.card_last4 ? `Card ${sub.card_brand} ••••${sub.card_last4}` : "No card on file"}${
                sub.current_period_end ? ` · period ends ${dateText(sub.current_period_end)}` : ""
              }`
            : "This tenant has no subscription yet. Choose a plan to start one."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Plan</Label>
            <div className="flex gap-2">
              <NativeSelect value={planId} onChange={setPlanId}>
                <option value="" disabled>
                  Choose a plan…
                </option>
                {tenant.plan_options.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.is_active ? "" : " (inactive)"}
                  </option>
                ))}
              </NativeSelect>
              <Button
                disabled={!planId || planId === (sub?.plan_id ?? "") || save.isPending}
                onClick={() => save.mutate({ plan_id: planId })}
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">A new plan's limits apply straight away.</p>
          </div>
          {sub && (
            <>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <NativeSelect value={sub.status} onChange={(v) => save.mutate({ status: v as typeof sub.status })}>
                  {SUB_STATUSES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div className="space-y-1.5">
                <Label>Billing interval</Label>
                <NativeSelect
                  value={sub.billing_interval}
                  onChange={(v) => save.mutate({ billing_interval: v as typeof sub.billing_interval })}
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </NativeSelect>
              </div>
            </>
          )}
        </div>
        {sub && (
          <>
            <div className="divide-y border-t">
              {switches.map((sw) => (
                <div key={sw.key} className="flex items-center justify-between gap-4 py-3">
                  <div>
                    <div className="text-sm font-medium">{sw.label}</div>
                    <div className="text-xs text-muted-foreground">{sw.help}</div>
                  </div>
                  <Switch checked={sub[sw.key]} disabled={save.isPending} onCheckedChange={(v) => save.mutate({ [sw.key]: v })} />
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <div className="space-y-1.5">
                <Label>Top-up threshold (USD)</Label>
                <Input type="number" step="0.01" min={0} className="w-40" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
              </div>
              <Button
                variant="outline"
                disabled={
                  save.isPending ||
                  !Number.isFinite(Number(threshold)) ||
                  Math.round(Number(threshold) * 100) === sub.auto_topup_threshold_cents
                }
                onClick={() => save.mutate({ auto_topup_threshold_cents: Math.round(Number(threshold) * 100) })}
              >
                Save threshold
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const LIMITS: { key: keyof QuotaLimits; used: keyof NonNullable<AdminTenantDetail["quota"]>; label: string }[] = [
  { key: "max_agents", used: "agents_count", label: "Agents" },
  { key: "max_phone_numbers", used: "phone_numbers_count", label: "Phone numbers" },
  { key: "max_team_members", used: "team_members_count", label: "Team members" },
  { key: "max_knowledge_documents", used: "knowledge_documents_count", label: "Knowledge documents" },
  { key: "included_minutes", used: "minutes_used", label: "Included minutes" },
];

function LimitsCard({ tenant }: { tenant: AdminTenantDetail }) {
  const saved = useTenantSaver(tenant.id);
  const quota = tenant.quota;
  const initial = Object.fromEntries(LIMITS.map((l) => [l.key, String(quota?.[l.key] ?? 0)])) as Record<
    keyof QuotaLimits,
    string
  >;
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [tenant.updated_at, JSON.stringify(quota)]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = LIMITS.some((l) => form[l.key] !== initial[l.key]);

  const save = useMutation({
    mutationFn: () =>
      platformApi.updateQuota(
        tenant.id,
        Object.fromEntries(LIMITS.map((l) => [l.key, Number(form[l.key])])) as QuotaLimits,
      ),
    onSuccess: (t) => {
      saved(t);
      toast.success("Limits saved.");
    },
    onError: (e) => toast.error(errText(e, "Couldn't save the limits.")),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Limits</CardTitle>
      </CardHeader>
      <CardContent>
        {!quota ? (
          <p className="text-sm text-muted-foreground">This tenant has no limits row.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {LIMITS.map((l) => (
                <div key={l.key} className="space-y-1.5">
                  <Label>{l.label}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form[l.key]}
                    onChange={(e) => setForm((f) => ({ ...f, [l.key]: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">{quota[l.used].toLocaleString()} in use</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save limits"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ApiKeysCard({ tenant }: { tenant: AdminTenantDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">API keys</CardTitle>
      </CardHeader>
      <CardContent>
        <ApiKeysPanel endpoints={platformApi.apiKeys(tenant.id)} />
      </CardContent>
    </Card>
  );
}

function MembersCard({ tenant }: { tenant: AdminTenantDetail }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Users ({tenant.members.length})</CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/admin/users?tenant=${tenant.id}&tenant_name=${encodeURIComponent(tenant.name)}`}>
            Manage in Users
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {tenant.members.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">No users in this tenant.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-medium">User</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenant.members.map((m) => (
                <tr key={m.membership_id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <Link to={`/admin/users/${m.user_id}`} className="font-medium hover:underline">
                      {fullName(m)}
                    </Link>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </td>
                  <td className="px-4 py-2 capitalize">
                    {m.role}
                    {!m.is_home && <span className="ml-2 text-xs text-muted-foreground">(extra workspace)</span>}
                  </td>
                  <td className="px-4 py-2">
                    {!m.user_active ? (
                      <Pill tone="red">Account disabled</Pill>
                    ) : !m.is_active ? (
                      <Pill tone="amber">Removed from tenant</Pill>
                    ) : (
                      <Pill tone="green">Active</Pill>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
